import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDebounce from '../../utils/useDebounce';
import { useCollapse } from 'react-collapsed';
import Header from '../../components/ui/Header';
import DashboardStats from './components/DashboardStats';
import RecentActivity from './components/RecentActivity';
import QuickActions from './components/QuickActions';
import { UserProfile } from './components/UserProfile';
import PurchaseHistory from './components/PurchaseHistory';
import WishlistManager from './components/WishlistManager';
import NotificationCenter from './components/NotificationCenter';
import MyReviewsTab from './components/MyReviewsTab';
import WalletManager from './components/WalletManager';
import SupportSessionsTab from './components/SupportSessionsTab';
import LoginHistoryModal from './components/LoginHistoryModal';
import PasswordResetModal from './components/PasswordResetModal';
import VisualSearchTab from './components/VisualSearchTab';
// SubscriptionPreferences will be integrated directly into Settings tab
// import SubscriptionPreferences from './components/SubscriptionPreferences';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { achievementService } from '../../services/achievementService';
import { userService } from '../../services/userService';
import { categoryService } from '../../services/categoryService';
import { manufacturerService } from '../../services/manufacturerService';
import { newsletterService, emailTemplateService } from '../../services/newsletterService';
import ReturnsIframeRenderer from '../../components/ui/ReturnsIframeRenderer';
import Footer from '../homepage/components/Footer';

const UserAccountDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [orderEmailPref, setOrderEmailPref] = useState('detailed');
  const { user, userProfile, refreshUserProfile } = useAuth();
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [achievementStats, setAchievementStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categoryPreferences, setCategoryPreferences] = useState([]);
  const [brandPreferences, setBrandPreferences] = useState([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const iframeRef = useRef(null);
  const chatIframeRef = useRef(null);

  // Track subtab view
  useEffect(() => {
    if (user?.id && activeTab) {
      userService.recordActivity(
        user.id,
        navigator.userAgent,
        'Subtab View',
        `/user-account-dashboard/${activeTab}`
      ).catch(err => console.warn('Activity record failed', err));
    }
  }, [activeTab, user?.id]);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [returnsIframeHeight, setReturnsIframeHeight] = useState('100%'); // Dynamic height from iframe

  // For Subscription Preferences within Settings
  const [topics, setTopics] = useState([]);
  const [userTopicPreferences, setUserTopicPreferences] = useState([]);
  const [newsletters, setNewsletters] = useState([]); // This will be email templates flagged as newsletters
  const [userNewsletterSubscriptions, setUserNewsletterSubscriptions] = useState([]);
  const { getCollapseProps: getSubCollapseProps, getToggleProps: getSubToggleProps, isExpanded: isSubExpanded } = useCollapse();

  const { getCollapseProps: getCatCollapseProps, getToggleProps: getCatToggleProps, isExpanded: isCatExpanded } = useCollapse();
  const { getCollapseProps: getBrandCollapseProps, getToggleProps: getBrandToggleProps, isExpanded: isBrandExpanded } = useCollapse();
  const { getCollapseProps: getSupportCollapseProps, getToggleProps: getSupportToggleProps, isExpanded: isSupportExpanded } = useCollapse({ defaultExpanded: false });

  // Sync activeTab with URL changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async (currentUser) => {
        // Individual fetchers with independent error handling
        const fetchStats = async () => {
            try { return await achievementService.getUserStats(currentUser.id); }
            catch (e) { console.error('Stats fetch failed', e); return null; }
        };
        const fetchTopics = async () => {
            try { return await newsletterService.getAllTopics(); }
            catch (e) { console.error('Topics fetch failed', e); return []; }
        };
        const fetchUserTopicPrefs = async () => {
            try { return await newsletterService.getContentPreferences(currentUser.id); }
            catch (e) { console.error('User topic prefs fetch failed', e); return []; }
        };
        const fetchNewsletters = async () => {
            try { return await emailTemplateService.getAllActive(); }
            catch (e) { console.error('Newsletters fetch failed', e); return []; }
        };
        const fetchUserNewsletterSubs = async () => {
            try { return await newsletterService.getNewsletterSubscriptions(currentUser.id); }
            catch (e) { console.error('User newsletter subs fetch failed', e); return []; }
        };

        try {
            const [cats, mnfs, stats, topicsData, userTopicPrefsData, newslettersData, userNewsletterSubsData] = await Promise.all([
                categoryService.getAll().catch(() => []),
                manufacturerService.getAll().catch(() => []),
                fetchStats(),
                fetchTopics(),
                fetchUserTopicPrefs(),
                fetchNewsletters(),
                fetchUserNewsletterSubs()
            ]);
            
            if (!isMounted) return;
            
            setCategories(cats || []);
            setBrands(mnfs || []);
            setAchievementStats(stats);
            setTopics(topicsData || []);
            setUserTopicPreferences(userTopicPrefsData || []);
            setNewsletters(newslettersData || []);
            setUserNewsletterSubscriptions(userNewsletterSubsData || []);
        } catch (err) {
            console.error('Error in dashboard Promise.all:', err);
        }
    };

    if (user?.id) {
        loadData(user);
    } else {
        // Fallback check if AuthContext user is delayed
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user && isMounted) {
                loadData(session.user);
            }
        });
    }
    
    return () => { isMounted = false; };
  }, [user]);

  useEffect(() => {
    if (userProfile?.raw_user_meta_data) {
      const meta = userProfile.raw_user_meta_data;
      setCategoryPreferences(meta.category_preferences || []);
      setBrandPreferences(meta.brand_preferences || []);
      if (meta.orderEmailPref) {
        setOrderEmailPref(meta.orderEmailPref);
      }
      // No longer explicitly loading topic preferences from raw_user_meta_data here
      // as they are managed via newsletterService.getContentPreferences/updateContentPreferences
    }
  }, [userProfile]);
  
  // Iframe message listener for dynamic height
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'returns-portal-resize' && event.data.height) {
        setReturnsIframeHeight(`${event.data.height}px`);
      }
      if (event.data && event.data.type === 'returns-portal-ready') {
        const postSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session && iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage({ type: 'supabase-session', payload: session }, '*');
            }
        };
        postSession();
      }
      if (event.data && event.data.type === 'chat-ready') {
        const postSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (chatIframeRef.current?.contentWindow) {
                chatIframeRef.current.contentWindow.postMessage({ type: 'supabase-session', payload: session || null }, '*');
            }
        };
        postSession();
      }
      if (event.data && event.data.type === 'close-chat') {
        setIsChatOpen(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleCategoryToggle = (categoryName) => {
    const newPrefs = categoryPreferences.includes(categoryName)
      ? categoryPreferences.filter((c) => c !== categoryName)
      : [...categoryPreferences, categoryName];
    setCategoryPreferences(newPrefs);
    savePreferences({ category_preferences: newPrefs });
  };

  const handleBrandToggle = (brandId) => {
    const newPrefs = brandPreferences.includes(brandId)
      ? brandPreferences.filter((id) => id !== brandId)
      : [...brandPreferences, brandId];
    setBrandPreferences(newPrefs);
    savePreferences({ brand_preferences: newPrefs });
  };

  // Handle topic preference change from UI
  const handleUserTopicChange = async (topicId, isEnabled) => {
    const updatedPreferences = [...userTopicPreferences];
    const existingPrefIndex = updatedPreferences.findIndex(p => p.contentType === topicId); // Backend maps ID to contentType

    if (existingPrefIndex > -1) {
      updatedPreferences[existingPrefIndex].isEnabled = isEnabled;
    } else {
      updatedPreferences.push({ contentType: topicId, isEnabled });
    }
    setUserTopicPreferences(updatedPreferences);
    
    // Save to backend via newsletterService
    const currentUserId = user?.id || (await supabase.auth.getSession()).data?.session?.user?.id;
    if (currentUserId) {
      await newsletterService.updateContentPreferences(currentUserId, updatedPreferences);
    }
  };

  // Handle newsletter subscription change from UI
  const handleUserNewsletterChange = async (newsletterTemplateId, isSubscribed) => {
    const updatedSubscriptions = [...userNewsletterSubscriptions];
    const existingSubIndex = updatedSubscriptions.findIndex(s => s.newsletterId === newsletterTemplateId);

    if (existingSubIndex > -1) {
      updatedSubscriptions[existingSubIndex].isSubscribed = isSubscribed;
    } else {
      updatedSubscriptions.push({ newsletterId: newsletterTemplateId, isSubscribed });
    }

    setUserNewsletterSubscriptions(updatedSubscriptions);
    
    // Save to backend via newsletterService
    const currentUserId = user?.id || (await supabase.auth.getSession()).data?.session?.user?.id;
    if (currentUserId) {
        await newsletterService.updateNewsletterSubscriptions(currentUserId, updatedSubscriptions);
    }
  };

  const debouncedSave = useDebounce(async (newMetadata) => {
    if (!user?.id) return;
    try {
      await supabase
        .from('users')
        .update({
          raw_user_meta_data: {
            ...userProfile.raw_user_meta_data,
            ...newMetadata,
            preferences_updated_at: new Date().toISOString(),
          },
        })
        .eq('id', user.id);
      await refreshUserProfile();
    } catch (err) {
      console.error('Error saving preferences:', err);
    }
  }, 1000);

  const savePreferences = (newPrefs) => {
    debouncedSave(newPrefs);
  };

  const filteredBrands = brands.filter((brand) =>
    brand.name.toLowerCase().includes(brandSearchTerm.toLowerCase())
  );

  const selectedBrands = brands.filter((brand) => brandPreferences.includes(brand.id));

  const filteredCategories = categories.filter((cat) =>
    cat.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
  );

  const selectedCategories = categories.filter((cat) => categoryPreferences.includes(cat.name));

  useEffect(() => {
    if (isChatOpen && chatIframeRef.current) {
       const postSession = async () => {
           const { data: { session } } = await supabase.auth.getSession();
           if (session && chatIframeRef.current?.contentWindow) {
               chatIframeRef.current.contentWindow.postMessage({ type: 'supabase-session', payload: session }, '*');
           }
       };
       postSession();
       setTimeout(postSession, 1500);
    }
  }, [isChatOpen]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'wishlist', label: 'Saved', icon: 'Heart' },
    { id: 'myReviews', label: 'My Reviews', icon: 'MessageSquare' },
    { id: 'purchases', label: 'Purchases', icon: 'ShoppingBag' },
    { id: 'returns', label: 'Returns', icon: 'Repeat' },
    { id: 'wallet', label: 'Store Credit', icon: 'Wallet' },
    { id: 'images', label: 'Images', icon: 'Camera' },
    { id: 'garage', label: 'My Garage Service', icon: 'Wrench' },
    // Subscriptions tab removed - now part of settings
    { id: 'settings', label: 'Settings', icon: 'Settings' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="lg:col-span-1 "><RecentActivity /></div>
            <div className="lg:col-span-1 lg:w-[600px]"><QuickActions /></div>
          </div>
        );
      case 'profile': return <UserProfile />;
      case 'purchases': return <PurchaseHistory />;
      case 'wallet': return <WalletManager />;
      case 'myReviews': return <MyReviewsTab />;
      case 'wishlist': return <WishlistManager />;
      case 'notifications': return <NotificationCenter />;
      case 'images': return <VisualSearchTab />;
      case 'garage': return <GarageConnectionsTab />;
      case 'returns': return <div className="flex flex-col h-full"><ReturnsIframeRenderer iframeHeight={returnsIframeHeight} isUserDashboard={true} iframeRef={iframeRef} /></div>;
      case 'settings':
        return (
          <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
            <h2 className="text-xl font-semibold text-foreground mb-6">Account Settings</h2>
            <div className="space-y-8">
              {/* Product Preferences */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Product Preferences</h3>
                <div className="space-y-4 pl-4 border-l-2">
                  <div>
                    <div {...getCatToggleProps()} className="flex justify-between items-center cursor-pointer">
                      <h4 className="text-sm font-medium text-foreground">Preferred Categories</h4>
                      <Icon name={isCatExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                    </div>
                    <section {...getCatCollapseProps()}>
                      <div className="mt-2 space-y-3">
                        <input type="text" placeholder="Search categories..." value={categorySearchTerm} onChange={(e) => setCategorySearchTerm(e.target.value)} className="w-full p-2 border rounded-lg bg-input" />
                        <div className="flex flex-wrap gap-2">{selectedCategories.map(c => <div key={c.id} className="bg-muted text-xs px-2 py-1 rounded-full flex items-center">{c.name} <button onClick={() => handleCategoryToggle(c.name)} className="ml-2 hover:text-destructive"><Icon name="X" size={12} /></button></div>)}</div>
                        <div className="max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pr-2">{filteredCategories.map(c => <label key={c.id} className="flex items-center p-2 rounded-lg hover:bg-muted/50"><input type="checkbox" className="w-4 h-4 text-primary rounded focus:ring-primary mr-3" checked={categoryPreferences.includes(c.name)} onChange={() => handleCategoryToggle(c.name)} />{c.name}</label>)}</div>
                      </div>
                    </section>
                  </div>
                  <div>
                    <div {...getBrandToggleProps()} className="flex justify-between items-center cursor-pointer">
                      <h4 className="text-sm font-medium text-foreground">Preferred Brands</h4>
                      <Icon name={isBrandExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                    </div>
                    <section {...getBrandCollapseProps()}>
                      <div className="mt-2 space-y-3">
                        <input type="text" placeholder="Search brands..." value={brandSearchTerm} onChange={(e) => setBrandSearchTerm(e.target.value)} className="w-full p-2 border rounded-lg bg-input" />
                        <div className="flex flex-wrap gap-2">{selectedBrands.map(b => <div key={b.id} className="bg-muted text-xs px-2 py-1 rounded-full flex items-center">{b.name} <button onClick={() => handleBrandToggle(b.id)} className="ml-2 hover:text-destructive"><Icon name="X" size={12} /></button></div>)}</div>
                        <div className="max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pr-2">{filteredBrands.map(b => <label key={b.id} className="flex items-center p-2 rounded-lg hover:bg-muted/50"><input type="checkbox" className="w-4 h-4 text-primary rounded focus:ring-primary mr-3" checked={brandPreferences.includes(b.id)} onChange={() => handleBrandToggle(b.id)} />{b.name}</label>)}</div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>

              {/* Privacy Settings */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="text-lg font-medium">Privacy & Sharing</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div><h4 className="text-sm font-medium">Profile Visibility</h4><p className="text-xs text-muted-foreground">Control who can see your profile and activity</p></div>
                    <select className="p-2 text-sm border rounded-lg bg-input"><option>Public</option><option>Friends Only</option><option>Private</option></select>
                  </div>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div><h4 className="text-sm font-medium">Assembly Sharing</h4><p className="text-xs text-muted-foreground">Allow others to see your model assemblies</p></div>
                    <label className="relative inline-flex items-center cursor-pointer"><input type="checkbox" className="sr-only peer" defaultChecked /><div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 peer-checked:bg-primary"></div></label>
                  </div>
                </div>
              </div>

              {/* Notification Preferences */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="text-lg font-medium">Notification Preferences</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 border rounded-lg"><div><h4 className="text-sm font-medium">Price Alerts</h4><p className="text-xs text-muted-foreground">Get notified when wishlist items go on sale</p></div><label className="relative inline-flex items-center cursor-pointer"><input type="checkbox" className="sr-only peer" defaultChecked /><div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 peer-checked:bg-primary"></div></label></div>
                  <div className="flex items-center justify-between p-4 border rounded-lg"><div><h4 className="text-sm font-medium">New Releases</h4><p className="text-xs text-muted-foreground">Updates about new models from followed creators</p></div><label className="relative inline-flex items-center cursor-pointer"><input type="checkbox" className="sr-only peer" defaultChecked /><div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 peer-checked:bg-primary"></div></label></div>
                  <div className="flex items-center justify-between p-4 border rounded-lg"><div><h4 className="text-sm font-medium">Community Activity</h4><p className="text-xs text-muted-foreground">Notifications about likes, comments, and follows</p></div><label className="relative inline-flex items-center cursor-pointer"><input type="checkbox" className="sr-only peer" defaultChecked /><div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 peer-checked:bg-primary"></div></label></div>
                  <div className="flex items-center justify-between p-4 border rounded-lg"><div><h4 className="text-sm font-medium">System Updates</h4><p className="text-xs text-muted-foreground">Important account and security notifications</p></div><label className="relative inline-flex items-center cursor-pointer"><input type="checkbox" className="sr-only peer" defaultChecked /><div className="w-11 h-6 bg-muted rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 peer-checked:bg-primary"></div></label></div>
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-indigo-50/30 border-indigo-100">
                    <div>
                      <h4 className="text-sm font-semibold text-indigo-900">Order Emails Notification</h4>
                      <p className="text-xs text-indigo-700 mt-0.5">Control how you receive emails for order creations, updates, or returns.</p>
                    </div>
                    <select
                      value={orderEmailPref}
                      onChange={(e) => {
                        const val = e.target.value;
                        setOrderEmailPref(val);
                        savePreferences({ orderEmailPref: val });
                      }}
                      className="p-2 pr-10 text-sm border border-indigo-200 rounded-lg bg-white text-indigo-900 font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    >
                      <option value="detailed">Detailed email</option>
                      <option value="simplified">Simplified email</option>
                      <option value="none">No email</option>
                    </select>
                  </div>

                  {/* Newsletters Section (Collapsible) */}
                  <div>
                    <div {...getSubToggleProps()} className="flex justify-between items-center cursor-pointer p-4 border rounded-lg">
                      <h4 className="text-sm font-medium text-foreground">Newsletters</h4>
                      <Icon name={isSubExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                    </div>
                    <section {...getSubCollapseProps()}>
                      <div className="mt-4 space-y-4 pl-4 border-l-2">
                        <div>
                          <h4 className="text-sm font-medium text-foreground mb-2">Topics</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {topics.map(topic => (
                              <label key={topic.id} className="flex items-center p-2 rounded-lg hover:bg-muted/50">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-primary rounded focus:ring-primary mr-3"
                                  checked={userTopicPreferences.some(p => p.contentType === topic.id && p.isEnabled)}
                                  onChange={(e) => handleUserTopicChange(topic.id, e.target.checked)}
                                />
                                {topic.name}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
              {/* Security */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="text-lg font-medium">Security</h3>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div><h4 className="text-sm font-medium">Password</h4><p className="text-xs text-muted-foreground">Update your account password</p></div>
                    <Button variant="outline" size="sm" onClick={() => setIsPasswordResetModalOpen(true)}>Reset Password</Button>
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div><h4 className="text-sm font-medium">Two-Factor Authentication</h4><p className="text-xs text-muted-foreground">Add an extra layer of security</p></div>
                    <Button variant="outline" size="sm">Enable</Button>
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div><h4 className="text-sm font-medium">Login History</h4><p className="text-xs text-muted-foreground">View recent login activity</p></div>
                    <Button variant="outline" size="sm" iconName="ExternalLink" onClick={() => setShowLoginHistory(true)}>View History</Button>
                </div>
              </div>
              {/* Support History (Collapsible) */}
              <div className="space-y-4 pt-6 border-t">
                <div {...getSupportToggleProps()} className="flex justify-between items-center cursor-pointer p-4 border rounded-lg hover:bg-muted/30 transition-all">
                  <div>
                    <h3 className="text-sm font-medium text-foreground">Support History</h3>
                    <p className="text-xs text-muted-foreground">View and search your past conversations</p>
                  </div>
                  <Icon name={isSupportExpanded ? 'ChevronUp' : 'ChevronDown'} size={18} />
                </div>
                <section {...getSupportCollapseProps()}>
                  <div className="mt-4">
                    <SupportSessionsTab />
                  </div>
                </section>
              </div>
              {/* Account Actions */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="text-lg font-medium">Account Actions</h3>
                <div className="flex flex-wrap gap-3">
                    <Button variant="outline" iconName="Download">Export Data</Button>
                    <Button variant="outline" iconName="RefreshCw">Reset Preferences</Button>
                    <Button variant="destructive" iconName="Trash2">Delete Account</Button>
                </div>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <div className="pt-16 flex-grow">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="mb-8"><DashboardStats achievementStats={achievementStats} /></div>
          <div className="mb-8">
            <nav className="flex space-x-8 border-b">
              {tabs.map((tab) => (
                <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSearchParams({tab: tab.id}); }} className={`flex items-center space-x-2 py-4 px-1 border-b-2 text-sm ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent'}`}>
                  <Icon name={tab.icon} size={16} /><span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
          <div className="flex-grow">{renderTabContent()}</div>
        </div>
      </div>
      <Footer />
      <LoginHistoryModal isOpen={showLoginHistory} onClose={() => setShowLoginHistory(false)} userId={user?.id} />
      <PasswordResetModal isOpen={isPasswordResetModalOpen} onClose={() => setIsPasswordResetModalOpen(false)} userEmail={user?.email} />
    </div>
  );
};

// Symmetrical B2C Garage Connections & Secure Diagnostics Tab
const GarageConnectionsTab = () => {
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [shopNames, setShopNames] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedVehicles, setCollapsedVehicles] = useState({});
  const [invoicePages, setInvoicePages] = useState({});
  const INVOICES_PER_PAGE = 25;

  const toggleVehicleCollapse = (vId) => {
    setCollapsedVehicles(p => ({ ...p, [vId]: !p[vId] }));
  };

  const loadGarageData = React.useCallback(async () => {
    if (!user?.email && !user?.id) return;
    try {
      setLoading(true);

      // 1. Query connection requests by email
      const { data: conns } = await supabase
        .from('shop_connections')
        .select('*, customer:shop_customers(*)')
        .ilike('client_email', user.email);
      if (conns) setConnections(conns);

      // 2. Resolve shop business names for each connection
      const shopUserIds = (conns || []).map(c => c.shop_id).filter(Boolean);
      const nameMap = {};
      if (shopUserIds.length > 0) {
        const { data: sellerAccounts } = await supabase
          .from('seller_accounts')
          .select('user_id, business_name, business_phone, business_email')
          .in('user_id', shopUserIds);
        sellerAccounts?.forEach(s => {
          nameMap[s.user_id] = s.business_name;
        });

        const { data: userAccounts } = await supabase
          .from('users')
          .select('id, first_name, last_name, email')
          .in('id', shopUserIds);
        userAccounts?.forEach(u => {
          if (!nameMap[u.id]) {
            const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
            nameMap[u.id] = fullName || u.email;
          }
        });
      }
      setShopNames(nameMap);

      // 3. Find all customer IDs linked to this user (via connections + direct shop_customers link)
      const connectionCustomerIds = (conns || []).map(c => c.customer_id).filter(Boolean);
      const { data: directCustomers } = await supabase
        .from('shop_customers')
        .select('id')
        .or(`linked_user_id.eq.${user.id},email.ilike.${user.email}`);

      const allCustIds = Array.from(new Set([
        ...connectionCustomerIds,
        ...(directCustomers || []).map(c => c.id)
      ]));

      // 4. Query linked assemblies/vehicles using verified product columns (gltf_file_path)
      let assembliesList = [];
      if (allCustIds.length > 0) {
        const { data: asms, error: asmsErr } = await supabase
          .from('customer_assemblies')
          .select(`
            id,
            customer_id,
            product_id,
            serial_number,
            notes,
            created_at,
            product:products(id, name, gltf_file_path, thumbnail_path)
          `)
          .in('customer_id', allCustIds);

        if (asmsErr) {
          console.error('Error fetching customer assemblies:', asmsErr);
        } else if (asms) {
          assembliesList = asms.map(a => ({
            ...a,
            product: a.product ? {
              ...a.product,
              modelUrl: a.product.gltf_file_path
            } : null
          }));
        }
      }

      // 5. Query complete service history records for all user's vehicles
      if (assembliesList.length > 0) {
        const assemblyIds = assembliesList.map(a => a.id);
        const { data: allRecords, error: recordsErr } = await supabase
          .from('assembly_service_records')
          .select('*')
          .in('assembly_id', assemblyIds);

        if (recordsErr) {
          console.error('Error fetching service records:', recordsErr);
        }

        const recMap = {};
        (allRecords || []).forEach(r => {
          if (!recMap[r.assembly_id]) recMap[r.assembly_id] = [];
          recMap[r.assembly_id].push(r);
        });

        assembliesList = assembliesList.map(a => ({
          ...a,
          service_records: (recMap[a.id] || []).sort((x, y) => new Date(y.date_serviced || y.created_at || 0) - new Date(x.date_serviced || x.created_at || 0))
        }));
      }

      setVehicles(assembliesList);
    } catch (err) {
      console.error('Error loading garage data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadGarageData();
  }, [loadGarageData]);

  const handleUpdateStatus = async (conn, status) => {
    try {
      // 1. Update status in shop_connections
      await supabase
        .from('shop_connections')
        .update({ status })
        .eq('id', conn.id);

      // 2. Symmetrically bind / unlink inside shop_customers
      const linkedId = status === 'approved' ? user.id : null;
      await supabase
        .from('shop_customers')
        .update({ linked_user_id: linkedId })
        .eq('id', conn.customer_id);

      alert(`Connection request successfully ${status}!`);
      loadGarageData();
    } catch (err) {
      console.error('Error updating connection status:', err);
    }
  };

  const handleUpdateInvoiceApproval = async (assemblyId, invoiceNo, isApproved) => {
    try {
      let query = supabase
        .from('assembly_service_records')
        .update({ 
          client_approved: isApproved,
          status: isApproved ? 'repaired' : 'needs_repair'
        })
        .eq('assembly_id', assemblyId);
      
      if (invoiceNo && invoiceNo !== 'General / Uncategorized Work') {
        query = query.eq('invoice_number', invoiceNo);
      }

      const { error } = await query;
      if (error) throw error;

      alert(isApproved ? `Work order / Invoice ${invoiceNo} approved!` : `Work order / Invoice ${invoiceNo} declined.`);
      loadGarageData();
    } catch (err) {
      console.error('Error updating invoice approval:', err);
      alert('Failed to update approval: ' + err.message);
    }
  };

  const handleUpdateItemApproval = async (recordId, isApproved) => {
    try {
      const { error } = await supabase
        .from('assembly_service_records')
        .update({ client_approved: isApproved })
        .eq('id', recordId);

      if (error) throw error;

      // Update local vehicle state optimistically
      setVehicles(prev => prev.map(v => ({
        ...v,
        service_records: (v.service_records || []).map(r => r.id === recordId ? { ...r, client_approved: isApproved } : r)
      })));
    } catch (err) {
      console.error('Error updating item approval:', err);
      alert('Failed to update item approval: ' + err.message);
    }
  };

  const handleOpenInvoiceReport = (assemblyId, invoiceNo) => {
    window.open(`/diagnostic-report?assemblyId=${assemblyId}&invoiceNo=${encodeURIComponent(invoiceNo)}`, '_blank');
  };

  if (loading) return <div className="text-center py-12"><Icon name="Wrench" className="animate-spin mx-auto text-primary mb-2" />Loading garage...</div>;

  return (
    <div className="space-y-6 text-left">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-1">My Garage Connections</h2>
        <p className="text-xs text-muted-foreground">Manage service garage authorization requests and review vehicle diagnostic reports and service invoices.</p>
      </div>

      {/* 1. Connection Requests Panel */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-soft">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-1.5">
          <Icon name="Bell" size={16} /> Connection Invitations
        </h3>
        <div className="space-y-3">
          {connections.map(c => {
            const shopDisplayName = shopNames[c.shop_id] || 'Authorized Repair Shop';
            const isApproved = c.status === 'approved';

            return (
              <div key={c.id} className="border border-border/80 rounded-xl p-4 bg-muted/10 flex flex-col sm:flex-row justify-between sm:items-center text-xs gap-3">
                <div className="space-y-1">
                  <h4 className="font-extrabold text-foreground uppercase tracking-wide flex items-center gap-1.5">
                    <Icon name="Wrench" size={14} className={isApproved ? "text-emerald-500" : "text-indigo-500"} />
                    {isApproved ? 'Connected Service Garage' : 'Secure Service Invitation'}
                  </h4>
                  <p className="text-muted-foreground">
                    Repair Shop: <span className="font-bold text-foreground text-sm">{shopDisplayName}</span>
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Registered Profile: <span className="font-medium text-foreground">"{c.customer?.name || 'Customer'}"</span>
                  </p>
                  <p className="text-[10px] text-slate-400">Date: {new Date(c.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0 self-start sm:self-auto">
                  {c.status === 'pending' ? (
                    <>
                      <button 
                        onClick={() => handleUpdateStatus(c, 'approved')}
                        className="px-3.5 py-1.5 rounded-lg text-[10px] font-black uppercase bg-emerald-600 hover:bg-emerald-500 text-white shadow-soft"
                      >
                        ✓ Accept Invitation
                      </button>
                      <button 
                        onClick={() => handleUpdateStatus(c, 'rejected')}
                        className="px-3.5 py-1.5 rounded-lg text-[10px] font-black uppercase bg-red-600 hover:bg-red-500 text-white shadow-soft"
                      >
                        Decline
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase border ${
                        c.status === 'approved' 
                          ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' 
                          : 'bg-red-500/10 text-red-500 border-red-500/20'
                      }`}>
                        {c.status === 'approved' ? '✓ Connected' : c.status}
                      </span>
                      {c.status === 'approved' && (
                        <button 
                          onClick={() => handleUpdateStatus(c, 'rejected')}
                          className="text-[10px] font-bold text-red-500 hover:underline pl-2 border-l border-border"
                        >
                          Disconnect
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {connections.length === 0 && (
            <p className="text-xs text-muted-foreground italic text-center py-6">No service garage invitations or link requests found.</p>
          )}
        </div>
      </div>

      {/* 2. Connected Vehicles & Service Invoices list */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-soft">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-1.5">
          <Icon name="Target" size={16} /> My Registered Vehicles & Service History
        </h3>
        <div className="space-y-5">
          {vehicles.map(v => {
            const records = v.service_records || [];
            const isCollapsed = !!collapsedVehicles[v.id];
            
            // Group service records by invoice number
            const invoiceGroups = {};
            records.forEach(r => {
              const invNum = r.invoice_number?.trim() || 'General / Uncategorized Work';
              if (!invoiceGroups[invNum]) invoiceGroups[invNum] = [];
              invoiceGroups[invNum].push(r);
            });

            // Pagination for Invoices (Limit 25 per page)
            const allInvoiceEntries = Object.entries(invoiceGroups);
            const totalInvoices = allInvoiceEntries.length;
            const currentPage = invoicePages[v.id] || 1;
            const totalPages = Math.ceil(totalInvoices / INVOICES_PER_PAGE) || 1;
            const pagedInvoices = allInvoiceEntries.slice((currentPage - 1) * INVOICES_PER_PAGE, currentPage * INVOICES_PER_PAGE);

            return (
              <div key={v.id} className="border border-border/80 rounded-xl p-5 bg-muted/10 text-xs space-y-4 shadow-xs">
                {/* Vehicle Header with Collapse Toggle */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border/60">
                  <div 
                    onClick={() => toggleVehicleCollapse(v.id)} 
                    className="min-w-0 cursor-pointer group flex items-start gap-2.5 flex-1"
                  >
                    <button 
                      type="button" 
                      className="mt-0.5 p-1 rounded-md text-muted-foreground group-hover:text-foreground hover:bg-muted/60 transition-colors"
                      title={isCollapsed ? "Expand vehicle details" : "Collapse vehicle details"}
                    >
                      <Icon name={isCollapsed ? "ChevronRight" : "ChevronDown"} size={16} />
                    </button>
                    <div className="min-w-0">
                      <h4 className="font-extrabold text-foreground uppercase tracking-wide truncate text-sm sm:text-base group-hover:text-primary transition-colors flex items-center gap-2">
                        {v.product?.name || 'Registered Vehicle / Asset'}
                      </h4>
                      <p className="text-muted-foreground mt-0.5">
                        Serial Number: <span className="font-mono text-foreground font-semibold">{v.serial_number || 'N/A'}</span>
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        {records.length} {records.length === 1 ? 'Service Record' : 'Service Records'} • {totalInvoices} {totalInvoices === 1 ? 'Work Order / Invoice' : 'Work Orders / Invoices'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 self-start sm:self-auto">
                    <button 
                      onClick={() => window.open(`/diagnostic-report?assemblyId=${v.id}`, '_blank')}
                      className="px-3.5 py-2 rounded-xl text-xs font-bold uppercase bg-indigo-600 hover:bg-indigo-500 text-white shadow-md flex items-center gap-1.5"
                      title="Open Complete 3D Diagnostic Report for all vehicle records"
                    >
                      <span>🚀 3D Inspection</span>
                    </button>
                  </div>
                </div>

                {/* Invoices & Work Orders List */}
                {!isCollapsed && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h5 className="text-[11px] font-bold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                        <Icon name="Receipt" size={13} className="text-primary" />
                        Invoices & Work Orders ({totalInvoices})
                      </h5>
                      {totalPages > 1 && (
                        <span className="text-[10px] text-muted-foreground font-semibold">
                          Page {currentPage} of {totalPages}
                        </span>
                      )}
                    </div>

                    {totalInvoices === 0 ? (
                      <p className="text-muted-foreground italic text-xs py-3 pl-2">No service orders or invoices recorded yet.</p>
                    ) : (
                      <>
                        {pagedInvoices.map(([invoiceNum, recs]) => {
                          const totalCost = recs.reduce((sum, r) => sum + (parseFloat(r.cost) || 0), 0);
                          const isAllApproved = recs.every(r => r.client_approved === true);
                          const isAnyDeclined = recs.some(r => r.client_approved === false);
                          const isPending = !isAllApproved && !isAnyDeclined;

                          return (
                            <div key={invoiceNum} className="border-2 border-slate-300 dark:border-slate-700/80 rounded-xl overflow-hidden bg-background shadow-md">
                              {/* High-Contrast Invoice Summary Header - Clickable to open 3D inspection view! */}
                              <div 
                                onClick={() => handleOpenInvoiceReport(v.id, invoiceNum)}
                                className="p-3.5 bg-slate-900 hover:bg-slate-800/95 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-white cursor-pointer transition-colors group"
                                title={`Click to view all items of ${invoiceNum} in the interactive 3D model`}
                              >
                                <div className="flex items-center gap-2.5 flex-wrap">
                                  <span className="px-2 py-0.5 rounded-md bg-indigo-950/90 border border-indigo-500/60 text-indigo-300 font-mono font-black text-[11px] tracking-wider uppercase">
                                    {invoiceNum.toUpperCase().includes('EST') ? 'Estimate' : 'Invoice'}
                                  </span>
                                  <span className="font-black text-sm text-white tracking-wide group-hover:text-indigo-200 transition-colors flex items-center gap-1.5">
                                    <Icon name="Receipt" size={14} className="text-indigo-400 group-hover:scale-110 transition-transform" />
                                    {invoiceNum}
                                  </span>
                                  <span className="text-emerald-400 font-black text-base ml-1 drop-shadow-xs">${totalCost.toFixed(2)}</span>
                                  
                                  {/* High-Contrast Status Badges */}
                                  {isAllApproved ? (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-1">
                                      <span>✓</span> Approved by You
                                    </span>
                                  ) : isAnyDeclined ? (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-500/20 text-rose-300 border border-rose-500/40 flex items-center gap-1">
                                      <span>✕</span> Item(s) Declined
                                    </span>
                                  ) : (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-amber-500/20 text-amber-300 border border-amber-500/40 flex items-center gap-1">
                                      <span>⏳</span> Pending Your Approval
                                    </span>
                                  )}
                                </div>

                                {/* Actions: High-Contrast 3D Report & Client Approval */}
                                <div 
                                  onClick={(e) => e.stopPropagation()} 
                                  className="flex items-center gap-2 flex-wrap self-start sm:self-auto"
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleOpenInvoiceReport(v.id, invoiceNum)}
                                    className="px-3 py-1.5 text-xs font-bold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white shadow-md flex items-center gap-1.5 transition-colors cursor-pointer"
                                    title={`View 3D Diagnostic Report strictly for Invoice ${invoiceNum}`}
                                  >
                                    <span>🚀 3D Inspection</span>
                                    <Icon name="ExternalLink" size={11} className="text-indigo-200" />
                                  </button>

                                  {isPending && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => handleUpdateInvoiceApproval(v.id, invoiceNum, true)}
                                        className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-lg shadow-md flex items-center gap-1 transition-colors cursor-pointer"
                                        title="Approve all items on this invoice"
                                      >
                                        <span>✓ Approve All</span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleUpdateInvoiceApproval(v.id, invoiceNum, false)}
                                        className="px-3.5 py-1.5 bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-500/40 font-bold text-xs rounded-lg transition-colors cursor-pointer"
                                        title="Decline all items on this invoice"
                                      >
                                        Decline All
                                      </button>
                                    </>
                                  )}
                                  {isAllApproved && (
                                    <span className="text-[11px] text-slate-300 font-medium px-2.5 py-1 bg-slate-800 rounded-md border border-slate-700">
                                      Work authorized
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Line Items for this Invoice */}
                              <div className="divide-y divide-border/80 bg-card">
                                {recs.map(rec => (
                                  <div key={rec.id} className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs hover:bg-muted/30 transition-colors">
                                    <div className="space-y-1 min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-extrabold text-foreground text-xs">
                                          {rec.component_id || 'General Repair / Inspection'}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                          rec.status === 'repaired' ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700' :
                                          rec.status === 'replaced' ? 'bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300 border border-blue-300 dark:border-blue-700' :
                                          'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700'
                                        }`}>
                                          {rec.status}
                                        </span>
                                      </div>
                                      {rec.notes && (
                                        <p className="text-muted-foreground text-xs leading-relaxed">{rec.notes}</p>
                                      )}
                                      {rec.resolution && (
                                        <p className="text-slate-500 dark:text-slate-400 text-[11px]">Resolution: {rec.resolution}</p>
                                      )}
                                    </div>

                                    {/* Price, Date, and Individual Item Approval Controls */}
                                    <div className="flex items-center gap-2.5 shrink-0 self-start sm:self-auto flex-wrap">
                                      {rec.cost > 0 && (
                                        <span className="font-black text-foreground text-sm">${parseFloat(rec.cost).toFixed(2)}</span>
                                      )}
                                      <span className="text-muted-foreground text-[11px] font-medium bg-muted/60 px-2 py-0.5 rounded border border-border/50">
                                        {new Date(rec.date_serviced || rec.created_at).toLocaleDateString()}
                                      </span>

                                      {/* Per-Item Customer Approval Controls */}
                                      <div className="flex items-center gap-1.5 ml-1">
                                        {rec.client_approved === true ? (
                                          <div className="flex items-center gap-1">
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                                              ✓ Authorized
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() => handleUpdateItemApproval(rec.id, false)}
                                              className="p-1 rounded text-muted-foreground hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                                              title="Decline this specific part/item"
                                            >
                                              <Icon name="X" size={12} />
                                            </button>
                                          </div>
                                        ) : rec.client_approved === false ? (
                                          <div className="flex items-center gap-1">
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-700">
                                              ✕ Declined
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() => handleUpdateItemApproval(rec.id, true)}
                                              className="p-1 rounded text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors"
                                              title="Authorize this specific part/item"
                                            >
                                              <Icon name="Check" size={12} />
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-1">
                                            <button
                                              type="button"
                                              onClick={() => handleUpdateItemApproval(rec.id, true)}
                                              className="px-2 py-1 rounded-md text-[10px] font-black bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs flex items-center gap-1 transition-colors cursor-pointer"
                                              title="Approve this specific part/item"
                                            >
                                              <Icon name="Check" size={11} />
                                              <span>Approve</span>
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => handleUpdateItemApproval(rec.id, false)}
                                              className="px-2 py-1 rounded-md text-[10px] font-bold bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-700 transition-colors cursor-pointer"
                                              title="Decline this specific part/item"
                                            >
                                              <Icon name="X" size={11} />
                                              <span>Decline</span>
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                        {/* Pagination Controls for Invoices (Limit 25 per page) */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-between pt-3 border-t border-border/80 text-xs text-muted-foreground">
                            <span>
                              Showing {(currentPage - 1) * INVOICES_PER_PAGE + 1} – {Math.min(currentPage * INVOICES_PER_PAGE, totalInvoices)} of {totalInvoices} Invoices
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                disabled={currentPage <= 1}
                                onClick={() => setInvoicePages(p => ({ ...p, [v.id]: (p[v.id] || 1) - 1 }))}
                                className="px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed font-medium text-xs"
                              >
                                Previous
                              </button>
                              <span className="px-2 font-bold text-foreground">
                                Page {currentPage} of {totalPages}
                              </span>
                              <button
                                type="button"
                                disabled={currentPage >= totalPages}
                                onClick={() => setInvoicePages(p => ({ ...p, [v.id]: (p[v.id] || 1) + 1 }))}
                                className="px-2.5 py-1 rounded-md border border-border bg-background hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed font-medium text-xs"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {vehicles.length === 0 && (
            <p className="text-xs text-muted-foreground italic text-center py-6">
              No registered service vehicles found for your account. When your connected repair shop adds your vehicle or logs work orders, they will appear here.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAccountDashboard;